import React, { useEffect, useMemo, useState } from "react";
import { db } from "@/config/firebase";
import { addDoc, collection, doc, getDocs, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { computeBetPoints, scoreDistance } from "@/utils/scoring";

export default function Bets() {
  const { user, role } = useAuth();
  const [bets, setBets] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);

  // Modal state
  const [show, setShow] = useState(false);
  const [gameId, setGameId] = useState("");
  const [home, setHome] = useState("");
  const [away, setAway] = useState("");
  const [tech, setTech] = useState("");

  const load = async () => {
    const bs = await getDocs(collection(db, "bets"));
    setBets(bs.docs.map((d) => ({ id: d.id, ...d.data() })));
    const ev = await getDocs(collection(db, "events"));
    // Fix kaputte Umlaute beim Laden
    const fixedEvents = ev.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        ...data,
        title: fixUmlauts(data.title),
        opponent: fixUmlauts(data.opponent),
        location: fixUmlauts(data.location),
        gym_name: fixUmlauts(data.gym_name),
      };
    });
    setEvents(fixedEvents);
    const us = await getDocs(collection(db, "users"));
    setUsers(us.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  // Helper: Repariere kaputte Umlaute (temporärer Fix)
  const fixUmlauts = (str) => {
    if (!str) return str;
    return String(str)
      .replace(/Ã¤/g, 'ä')
      .replace(/Ã¼/g, 'ü')
      .replace(/Ã¶/g, 'ö')
      .replace(/Ã„/g, 'Ä')
      .replace(/Ãœ/g, 'Ü')
      .replace(/Ã–/g, 'Ö')
      .replace(/ÃŸ/g, 'ß')
      .replace(/KÄln/g, 'Köln')
      .replace(/SÄdwest/g, 'Südwest')
      .replace(/DÄren/g, 'Düren')
      .replace(/MÄnchen/g, 'München');
  };

  useEffect(() => { load(); }, []);

  // Prüfe ob User bereits getippt hat
  const getMyBet = (gameId) => {
    return bets.find(b => b.game_id === gameId && b.user_id === user?.uid);
  };

  const hasBet = (gameId) => {
    return !!getMyBet(gameId);
  };

  // Filter scheduled games - nur zukünftige Spiele
  const scheduledGames = useMemo(() => {
    const now = new Date();
    return events
      .filter((e) => {
        if (e.type !== "game") return false;
        if (e.status === "final") return false; // Finalisierte Games ausschließen
        
        // Parse start_datetime
        let dt;
        if (e.start_datetime?.toDate) {
          dt = e.start_datetime.toDate();
        } else if (typeof e.start_datetime === "string") {
          dt = new Date(e.start_datetime);
        }
        
        // Nur zukünftige Games anzeigen
        return dt && dt >= now;
      })
      .sort((a, b) => {
        const aDate = a.start_datetime?.toDate?.() || new Date(a.start_datetime);
        const bDate = b.start_datetime?.toDate?.() || new Date(b.start_datetime);
        return aDate - bDate;
      });
  }, [events]);
  
  const finalGames = useMemo(() => events.filter((e) => e.type === "game" && e.status === "final"), [events]);

  const usersById = useMemo(() => Object.fromEntries(users.map((u) => [u.auth_uid || u.id, u])), [users]);

  const openBet = (gid) => {
    setGameId(gid);
    setShow(true);
    
    // Vorhandenen Tipp laden
    const existingBet = getMyBet(gid);
    if (existingBet) {
      setHome(String(existingBet.predicted_home_score || ""));
      setAway(String(existingBet.predicted_away_score || ""));
      setTech(String(existingBet.predicted_technicals || ""));
    } else {
      setHome("");
      setAway("");
      setTech("");
    }
  };

  const closeBet = () => { setShow(false); setGameId(""); setHome(""); setAway(""); setTech(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!user || !gameId) return;
    
    const existingBet = getMyBet(gameId);
    
    if (existingBet) {
      // UPDATE existierenden Tipp
      await updateDoc(doc(db, "bets", existingBet.id), {
        predicted_home_score: Number(home),
        predicted_away_score: Number(away),
        predicted_technicals: Number(tech),
        updated_at: serverTimestamp()
      });
    } else {
      // NEUEN Tipp erstellen
      await addDoc(collection(db, "bets"), {
        user_id: user.uid,
        game_id: gameId,
        predicted_home_score: Number(home),
        predicted_away_score: Number(away),
        predicted_technicals: Number(tech),
        points_awarded: 0,
        created_at: serverTimestamp()
      });
    }
    
    closeBet();
    await load();
  };

  // Leaderboard aggregation
  const leaderboard = useMemo(() => {
    const finalMap = Object.fromEntries(finalGames.map((g) => [g.id, g]));
    const agg = new Map();
    for (const b of bets) {
      const g = finalMap[b.game_id];
      const uid = b.user_id;
      const entry = agg.get(uid) || { points: 0, correctWins: 0, deviations: [], count: 0 };
      let pts = Number(b.points_awarded || 0);
      if (!pts && g) {
        const real = { h: Number(g.final_home_score||0), a: Number(g.final_away_score||0), t: Number(g.final_technicals||0) };
        const pred = { h: Number(b.predicted_home_score||0), a: Number(b.predicted_away_score||0), t: Number(b.predicted_technicals||0) };
        pts = computeBetPoints(pred, real);
      }
      entry.points += pts;
      if (g) {
        const predWinner = b.predicted_home_score === b.predicted_away_score ? 0 : b.predicted_home_score > b.predicted_away_score ? 1 : -1;
        const realWinner = g.final_home_score === g.final_away_score ? 0 : g.final_home_score > g.final_away_score ? 1 : -1;
        if (predWinner === realWinner) entry.correctWins += 1;
        entry.deviations.push(Math.abs(b.predicted_home_score - g.final_home_score) + Math.abs(b.predicted_away_score - g.final_away_score));
      }
      entry.count += 1;
      agg.set(uid, entry);
    }
    const rows = Array.from(agg.entries()).map(([uid, v]) => ({
      uid,
      name: usersById[uid]?.display_name || usersById[uid]?.name || usersById[uid]?.email || uid.slice(0,6)+"…",
      points: v.points,
      correctWins: v.correctWins,
      avgDeviation: v.deviations.length ? (v.deviations.reduce((a,b)=>a+b,0)/v.deviations.length) : 0,
      count: v.count,
    }));
    rows.sort((a,b)=>b.points - a.points);
    return rows.slice(0, 50);
  }, [bets, finalGames, usersById]);

  const resetSeason = async () => {
    if (role !== "admin") return;
    const year = new Date().getFullYear();
    for (const g of finalGames.filter(g=> (g.start_datetime?.toDate?.()?.getFullYear?.()||year) === year)) {
      const bs = bets.filter(b=>b.game_id===g.id);
      for (const b of bs) { try { await updateDoc(doc(db, "bets", b.id), { points_awarded: 0 }); } catch(_){} }
    }
    await load();
  };

  return (
    <div className="pb-28 min-h-screen bg-[#0e0b0c] text-rose-50 px-4 pt-6">
      <h2 className="text-xl font-bold mb-3">Wetten</h2>

      {/* Link zum vollständigen Leaderboard - OBEN */}
      <div className="mb-4">
        <Link to="/bets/leaderboard">
          <Button className="bg-[#b40023] hover:bg-[#97001d] w-full">
            📊 Vollständiges Leaderboard anzeigen
          </Button>
        </Link>
      </div>

      {/* Scheduled games to bet */}
      <div className="mb-4">
        <h3 className="font-semibold mb-2">Anstehende Spiele</h3>
        {scheduledGames.length===0 ? (
          <div className="text-sm text-rose-200/80" data-testid="empty-scheduled">Keine anstehenden Spiele.</div>
        ) : (
          <ul className="space-y-3" data-testid="scheduled-list">
            {scheduledGames.map(g=>{
              const dt = g.start_datetime?.toDate?.() || (typeof g.start_datetime==="string"? new Date(g.start_datetime): null);
              const when = dt ? `${dt.toLocaleDateString('de-DE')} ${dt.toLocaleTimeString('de-DE', {hour:'2-digit', minute:'2-digit'})}`: "";
              const isExpanded = show && gameId === g.id;
              const myBet = getMyBet(g.id);
              const alreadyBet = !!myBet;
              
              return (
                <li key={g.id} className={`rounded-lg border overflow-hidden ${
                  alreadyBet 
                    ? 'bg-green-950/20 border-green-500/30' 
                    : 'bg-[#1a0d11] border-white/10'
                }`}>
                  <div className="p-3 flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-medium flex items-center gap-2">
                        {g.title || g.opponent || "Spiel"}
                        {alreadyBet && <span className="text-xs bg-green-700 px-2 py-0.5 rounded-full">✓ Getippt</span>}
                      </div>
                      <div className="text-xs text-rose-200/80">{when}</div>
                      {alreadyBet && !isExpanded && (
                        <div className="text-xs text-green-400 mt-1">
                          Dein Tipp: {myBet.predicted_home_score} : {myBet.predicted_away_score} (T's: {myBet.predicted_technicals || 0})
                        </div>
                      )}
                    </div>
                    {(() => {
                      // Berechtigungsprüfung: Nur Spieler und Admin können wetten
                      const canBet = role === 'admin' || role === 'player';
                      
                      if (!canBet) {
                        return (
                          <div className="text-xs text-yellow-400 bg-yellow-900/20 p-2 rounded">
                            🎉 {role === 'fan' ? 'Fans können nicht wetten' : 'Sparringspartner können nicht wetten'}
                          </div>
                        );
                      }
                      
                      return (
                        <Button 
                          data-testid={`bet-open-${g.id}`} 
                          onClick={()=>openBet(g.id)} 
                          className={alreadyBet ? "bg-green-700 hover:bg-green-800" : "bg-[#b40023] hover:bg-[#97001d]"}
                        >
                          {alreadyBet ? 'Tipp ändern' : 'Tipp abgeben'}
                        </Button>
                      );
                    })()}
                  </div>
                  
                  {/* Bet form direkt unter dem Spiel */}
                  {isExpanded && (
                    <form onSubmit={submit} className="p-4 border-t border-white/10 bg-[#160b0f]" data-testid="bet-modal">
                      <h4 className="font-semibold mb-2 text-sm">
                        {alreadyBet ? 'Tipp ändern' : 'Tipp speichern'}
                      </h4>
                      <div className="grid grid-cols-3 gap-2">
                        <Input data-testid="bet-home" placeholder="Heim" value={home} onChange={(e)=>setHome(e.target.value)} className="bg-[#2a1419] border-white/10 text-rose-50" />
                        <Input data-testid="bet-away" placeholder="Auswärts" value={away} onChange={(e)=>setAway(e.target.value)} className="bg-[#2a1419] border-white/10 text-rose-50" />
                        <Input data-testid="bet-tech" placeholder="Tech" value={tech} onChange={(e)=>setTech(e.target.value)} className="bg-[#2a1419] border-white/10 text-rose-50" />
                        <Button data-testid="bet-submit" type="submit" className="col-span-2 bg-[#b40023] hover:bg-[#97001d]">
                          {alreadyBet ? '✓ Ändern' : 'Speichern'}
                        </Button>
                        <Button data-testid="bet-cancel" type="button" onClick={closeBet} className="bg-white/10 hover:bg-white/20">Abbrechen</Button>
                      </div>
                    </form>
                  )}
                </li>
              )})}
          </ul>
        )}
      </div>

    </div>
  );
}
